import { useMemo, useState, useEffect } from "react";
import { Plus, Sparkles, FileText, Trash2, Download, Search } from "lucide-react";
import { api } from "@/lib/api";

// Document types we surface as quick-pick options.
const DOC_TYPES = [
    "Owner's Manual",
    "Engine",
    "Electronics",
    "Wiring Diagram",
    "Plumbing",
    "Other",
];

// Manual-flavoured Reference view. Reuses the entries collection on the
// backend (category="Manuals") so we get free CRUD + AI extraction reuse,
// but renders a card grid optimised for PDFs.
export default function ManualsView({ vessel, entries, canWrite, currentUserId, onAdd, onEdit, onDelete, onExtractIntervals }) {
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return entries;
        return entries.filter((e) =>
            [e.title, e.notes, e.vendor, ...(Object.values(e.custom_data || {}))]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(s))
        );
    }, [entries, q]);

    return (
        <div data-testid="manuals-view">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 14, marginBottom: 20 }}>
                <div>
                    <h2 className="bm-serif" style={{ fontSize: 32, margin: 0, fontWeight: 500 }}>Manuals</h2>
                    <p style={{ color: "var(--bm-text-muted)", margin: "4px 0 0", fontSize: 14 }}>
                        Owner's manuals, engine docs, wiring diagrams · {entries.length} {entries.length === 1 ? "document" : "documents"}
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <div style={{ position: "relative" }}>
                        <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--bm-text-dim)" }} />
                        <input
                            data-testid="manuals-search-input"
                            className="bm-input"
                            placeholder="Search..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            style={{ maxWidth: 220, paddingLeft: 30 }}
                        />
                    </div>
                    <button data-testid="manuals-add-btn" className="bm-btn bm-btn-primary" onClick={onAdd} disabled={!canWrite} style={{ opacity: canWrite ? 1 : 0.4, cursor: canWrite ? "pointer" : "not-allowed" }}>
                        <Plus size={15} /> Upload manual
                    </button>
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="bm-card" data-testid="manuals-empty" style={{ padding: 48, textAlign: "center", color: "var(--bm-text-muted)" }}>
                    {entries.length === 0
                        ? "No manuals yet — upload a PDF owner's manual, engine doc, or wiring diagram."
                        : "No manuals match that search."}
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 14 }}>
                    {filtered.map((e) => (
                        <ManualCard
                            key={e.id}
                            entry={e}
                            canWrite={canWrite}
                            isOwner={currentUserId === e.added_by_user_id}
                            onEdit={() => onEdit(e)}
                            onDelete={() => onDelete(e.id)}
                            onExtractIntervals={() => onExtractIntervals(e)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ManualCard({ entry, canWrite, onEdit, onDelete, onExtractIntervals }) {
    const photos = entry.photos || (entry.photo_data ? [{ data: entry.photo_data, filename: entry.photo_filename }] : []);
    const firstFile = photos[0];
    const isPdf = firstFile?.data?.startsWith("data:application/pdf");
    const docType = entry.custom_data?.doc_type || "";
    const brand = entry.custom_data?.brand || "";
    const model = entry.custom_data?.model || "";
    const year = entry.custom_data?.year || "";

    const download = (ev) => {
        ev.stopPropagation();
        if (!firstFile?.data) return;
        const a = document.createElement("a");
        a.href = firstFile.data;
        a.download = firstFile.filename || `${entry.title || "manual"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    return (
        <div
            data-testid={`manual-card-${entry.id}`}
            className="bm-card"
            style={{ padding: 16, cursor: canWrite ? "pointer" : "default", display: "flex", flexDirection: "column", gap: 12, minHeight: 160, position: "relative" }}
            onClick={canWrite ? onEdit : undefined}
        >
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ flexShrink: 0, width: 48, height: 60, borderRadius: 6, background: isPdf ? "rgba(212,181,114,0.12)" : "rgba(91,185,232,0.1)", border: `1px solid ${isPdf ? "rgba(212,181,114,0.4)" : "var(--bm-border-soft)"}`, display: "grid", placeItems: "center", color: isPdf ? "var(--bm-gold)" : "var(--bm-blue)" }}>
                    <FileText size={26} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, lineHeight: 1.3, overflowWrap: "anywhere" }}>{entry.title}</div>
                    {docType && (
                        <div style={{ fontSize: 11, color: "var(--bm-gold)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>
                            {docType}
                        </div>
                    )}
                    {(brand || model || year) && (
                        <div style={{ fontSize: 12, color: "var(--bm-text-muted)", overflowWrap: "anywhere" }}>
                            {[brand, model, year].filter(Boolean).join(" · ")}
                        </div>
                    )}
                </div>
            </div>

            {entry.notes && (
                <div style={{ fontSize: 13, color: "var(--bm-text-muted)", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {entry.notes}
                </div>
            )}

            <div style={{ display: "flex", gap: 6, marginTop: "auto", flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
                {firstFile?.data && (
                    <button
                        type="button"
                        data-testid={`manual-download-${entry.id}`}
                        className="bm-btn bm-btn-ghost"
                        onClick={download}
                        style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                        <Download size={12} /> Open PDF
                    </button>
                )}
                {canWrite && isPdf && (
                    <button
                        type="button"
                        data-testid={`manual-extract-${entry.id}`}
                        className="bm-btn bm-btn-ghost"
                        onClick={(e) => { e.stopPropagation(); onExtractIntervals(); }}
                        title="Use AI to scan this manual for service intervals and queue them as Maintenance entries"
                        style={{ padding: "4px 10px", fontSize: 12 }}
                    >
                        <Sparkles size={12} color="var(--bm-gold)" /> Find intervals
                    </button>
                )}
                {canWrite && (
                    <button
                        type="button"
                        data-testid={`manual-delete-${entry.id}`}
                        className="bm-btn bm-btn-danger"
                        onClick={(ev) => { ev.stopPropagation(); onDelete(); }}
                        style={{ padding: "4px 8px", marginLeft: "auto" }}
                    >
                        <Trash2 size={12} />
                    </button>
                )}
            </div>
        </div>
    );
}

// Maintenance-interval extraction modal — opens when user clicks "Find
// intervals" on a manual card. Calls a future /api/manuals/extract-intervals
// endpoint (stub for now — shows a "coming soon" notice with a clear path
// to manual entry). This keeps the UI promise present without shipping a
// half-baked AI flow.
export function ManualIntervalsModal({ manual, onClose }) {
    return (
        <div data-testid="manual-intervals-modal" className="bm-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "grid", placeItems: "center", padding: 20, zIndex: 100 }} onClick={onClose}>
            <div className="bm-card" style={{ padding: 28, maxWidth: 520, width: "100%" }} onClick={(e) => e.stopPropagation()}>
                <h3 className="bm-serif" style={{ fontSize: 22, margin: 0, marginBottom: 10, fontWeight: 500 }}>
                    <Sparkles size={16} color="var(--bm-gold)" style={{ verticalAlign: -2, marginRight: 6 }} />
                    Find service intervals
                </h3>
                <p style={{ fontSize: 14, color: "var(--bm-text-muted)", lineHeight: 1.6 }}>
                    Coming in v1.4 — AI will scan <strong style={{ color: "var(--bm-text)" }}>{manual.title}</strong> for service intervals
                    (oil changes, impeller swaps, zinc replacements, etc.) and offer to queue them as scheduled Maintenance entries.
                </p>
                <p style={{ fontSize: 13, color: "var(--bm-text-dim)", marginTop: 12 }}>
                    For now, add the maintenance items manually under the Maintenance tab.
                </p>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
                    <button className="bm-btn bm-btn-primary" onClick={onClose}>Got it</button>
                </div>
            </div>
        </div>
    );
}
